/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Generate an array of positive integers around a given `median` ensuring no value below zero or above max is returned
 *
 * ```
 * > around(10, 5, 20)
 * [5,  6,  7,  8, 9, 10, 11, 12, 13, 14, 15]
 *
 * > around(3, 5, 20)
 * [0, 1, 2, 3, 4, 5, 6, 7, 8]
 *
 * > around(17, 5, 20)
 * [12, 13, 14, 15, 16, 17, 18, 19, 20]
 * ```
 *
 * @param median The median around which to return `n` integer
 * @param n The amount of integers to return before and after `median`
 * @param max The maximum value that can be returned in the integer array
 * @returns An array of integers
 */
export function around(median: number, n: number, max: number): number[] {
  const o: { [k: string]: boolean } = {};
  o[Math.max(Math.min(median, max), 0)] = true;
  for (let i = 1; i <= n; i++) {
    o[Math.max(0, median - i).toString()] = true;
  }
  for (let i = 1; i <= n; i++) {
    o[Math.min(median + i, max).toString()] = true;
  }
  return Object.keys(o).map((s) => parseInt(s, 10));
}

/** Debounce options */
export type DebounceOptions = {
  /** How long to wait (in millis) before a function can be invoked again */
  wait?: number;
};

/**
 * A basic debounce function
 * @param f The function that should be debounced
 * @param options Debounce options
 * @returns A function that will only execute the given `f` every `options.wait` milliseconds if continuously invoked.
 */
export function debounce<T>(
  f: (...args: any[]) => T,
  options?: DebounceOptions,
): (...args: unknown[]) => void {
  const wait = options?.wait ?? 1;
  let lastInvoke = 0;

  let timer = false;

  function timedInvoke(...args: unknown[]) {
    timer = false;
    lastInvoke = Date.now();
    f.apply({}, args);
  }

  return (...args: unknown[]) => {
    const now = Date.now();
    if (now > lastInvoke + wait) {
      lastInvoke = now;
      f.apply({}, args);
    } else if (!timer) {
      setTimeout(timedInvoke, wait, args);
      timer = true;
    }
  };
}
